import { UserRole } from '@/apolloClient.types';
import { useUser } from '@/contexts/UserSession';

const useUserManagementAbilities = () => {
    const user = useUser(true);
    const role = user.__typename === 'TenantUser' ? user.role : null;
    const company = user.__typename === 'TenantUser' ? user.company : null;

    const canViewAllUsers = () => {
        // CompanyAdmin, CourseAdmin, and Trainer can view users
        return !!company && [UserRole.CompanyAdmin, UserRole.CourseAdmin, UserRole.Trainer].includes(role!);
    };

    const canViewInvitations = () => {
        return !!company && role === UserRole.CompanyAdmin;
    };

    const canCreateOrEditUser = () => {
        // CompanyAdmin of their own company can create/edit users.
        return !!company && role === UserRole.CompanyAdmin;
    };

    const canViewGroups = () => {
        // All authenticated users can view groups (filtered by backend)
        return !!company && [UserRole.CompanyAdmin, UserRole.CourseAdmin, UserRole.Trainer].includes(role!);
    };

    const canCreateGroups = () => {
        return !!company && role === UserRole.CompanyAdmin;
    };

    const canEditGroups = () => {
        return !!company && role === UserRole.CompanyAdmin;
    };

    const canDeleteGroups = () => {
        return !!company && role === UserRole.CompanyAdmin;
    };

    const canManageGroupMembers = () => {
        return !!company && role === UserRole.CompanyAdmin;
    };

    const displayUserManagementMenuItem = () => {
        return canViewAllUsers() || canViewInvitations() || canViewGroups();
    };

    return {
        canViewAllUsers,
        canViewInvitations,
        canCreateOrEditUser,
        canViewGroups,
        canCreateGroups,
        canEditGroups,
        canDeleteGroups,
        canManageGroupMembers,
        displayUserManagementMenuItem,
    };
};

export type UserManagementAbilities = ReturnType<typeof useUserManagementAbilities>;
export default useUserManagementAbilities;
